(() => {
  const HIDDEN_CLASS = 'variant-gallery-hidden';

  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function getGalleryRoots() {
    return Array.from(document.querySelectorAll('.shopify-section-main-product-inner[data-prodify]'));
  }

  function getSelectedOptions(root) {
    const options = [];
    root.querySelectorAll('[data-prodify-option-container] input[type="radio"]:checked').forEach((input) => {
      options.push(input.value);
    });

    if (options.length) {
      return options;
    }

    root.querySelectorAll('[data-prodify-option-container] select').forEach((select) => {
      options.push(select.value);
    });

    return options;
  }

  function getVariantId(root) {
    if (window.prodify && window.prodify.currentVariant && window.prodify.currentVariant.id) {
      return String(window.prodify.currentVariant.id);
    }

    const variantsEl = root.querySelector('[data-prodify-variants-json]');
    const selectedOptions = getSelectedOptions(root);

    if (variantsEl && selectedOptions.length) {
      const variants = parseJson(variantsEl);
      if (Array.isArray(variants)) {
        const match = variants.find((variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selectedOptions.length &&
          variant.options.every((option, index) => option === selectedOptions[index])
        );

        if (match) {
          return String(match.id);
        }
      }
    }

    const idInput = root.querySelector('form[data-prodify-product-form] input[name="id"]');
    return idInput ? String(idInput.value) : null;
  }

  function mediaBelongsToVariant(element, variantId) {
    const taggedIds = (element.getAttribute('data-variant-ids') || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    // If there are no variant IDs associated with this media item, it is a common image and belongs to all variants.
    if (taggedIds.length === 0) {
      return true; 
    }
    return taggedIds.includes(String(variantId));
  }

  function getGalleryComponents(root) {
    if (!window.Alpine) return [];
    return Array.from(root.querySelectorAll('[x-data^="productGallery"]'))
      .map((el) => window.Alpine.$data(el))
      .filter(Boolean);
  }

  function setGalleryIndex(root, totalVisible, activeIndex) {
    getGalleryComponents(root).forEach((comp) => {
      comp.totalImages = Math.max(1, totalVisible);
      if (typeof comp.scrollToImage === 'function') {
        comp.scrollToImage(activeIndex, false);
      } else if (typeof comp.goTo === 'function') {
        comp.goTo(activeIndex);
      }
    });
  }

  function updateThumbStates(root, activeIndex) {
    root.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
      const visibleIndex = button.getAttribute('data-visible-index');
      const isVisible = visibleIndex !== null && !button.classList.contains(HIDDEN_CLASS);
      const isActive = isVisible && Number(visibleIndex) === activeIndex;

      button.classList.toggle('variant-gallery-thumb-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function updateDotStates(root, activeIndex) {
    root.querySelectorAll('.product-gallery__dot').forEach((dot) => {
      const visibleIndex = dot.getAttribute('data-visible-index');
      const isVisible = visibleIndex !== null && !dot.classList.contains(HIDDEN_CLASS);
      dot.classList.toggle('is-active', isVisible && Number(visibleIndex) === activeIndex);
    });
  }

  function syncLightboxVisibility(root, lightboxIndex) {
    root.querySelectorAll('[data-lightbox-media]').forEach((image) => {
      const visibleIndex = image.getAttribute('data-visible-index');
      const show =
        visibleIndex !== null &&
        !image.classList.contains(HIDDEN_CLASS) &&
        Number(visibleIndex) === lightboxIndex;
      image.style.display = show ? '' : 'none';
    });
  }

  function patchGalleryNavigation(root) {
    getGalleryComponents(root).forEach((comp) => {
      if (comp.__variantGalleryPatched) return;
      comp.__variantGalleryPatched = true;

      comp.isThumbActive = function(loopIndex, el) {
        if (!root.querySelector('[data-variant-gallery-map]')) {
          return this.activeIndex === loopIndex;
        }
        if (el) {
          const visibleIndex = el.getAttribute('data-visible-index');
          return visibleIndex !== null && Number(visibleIndex) === this.activeIndex;
        }
        return false;
      };

      const originalOnViewportScroll = comp.onViewportScroll?.bind(comp);

      if (originalOnViewportScroll) {
        comp.onViewportScroll = function onViewportScroll() {
          originalOnViewportScroll();
          updateThumbStates(root, this.activeIndex);
          updateDotStates(root, this.activeIndex);
        };
      }

      if (typeof comp.goTo === 'function') {
        const originalGoTo = comp.goTo.bind(comp);
        comp.goTo = function goTo(index) {
          originalGoTo(index);
          updateThumbStates(root, this.activeIndex);
          updateDotStates(root, this.activeIndex);
        };
      }

      if (typeof comp.prev === 'function') {
        const originalPrev = comp.prev.bind(comp);
        comp.prev = function prev() {
          originalPrev();
          updateThumbStates(root, this.activeIndex);
          updateDotStates(root, this.activeIndex);
        };
      }

      if (typeof comp.next === 'function') {
        const originalNext = comp.next.bind(comp);
        comp.next = function next() {
          originalNext();
          updateThumbStates(root, this.activeIndex);
          updateDotStates(root, this.activeIndex);
        };
      }

      if (typeof comp.openLightbox === 'function') {
        comp.openLightbox = function openLightbox(index) {
          const nextIndex = index !== undefined ? index : this.activeIndex;
          this.lightboxIndex = nextIndex;
          this.lightboxOpen = true;
          document.body.style.overflow = 'hidden';
          syncLightboxVisibility(root, nextIndex);
        };
      }

      if (typeof comp.lightboxPrev === 'function') {
        comp.lightboxPrev = function lightboxPrev() {
          if (this.lightboxIndex > 0) {
            this.lightboxIndex -= 1;
            syncLightboxVisibility(root, this.lightboxIndex);
          }
        };
      }

      if (typeof comp.lightboxNext === 'function') {
        comp.lightboxNext = function lightboxNext() {
          if (this.lightboxIndex < this.totalImages - 1) {
            this.lightboxIndex += 1;
            syncLightboxVisibility(root, this.lightboxIndex);
          }
        };
      }

      if (typeof comp.closeLightbox === 'function') {
        comp.closeLightbox = function closeLightbox() {
          this.lightboxOpen = false;
          document.body.style.overflow = '';
        };
      }
    });
  }

  function getFeaturedVisibleIndex(root, variantId) {
    const featuredId =
      window.prodify &&
      window.prodify.currentVariant &&
      window.prodify.currentVariant.featured_media &&
      window.prodify.currentVariant.featured_media.id;

    if (featuredId) {
      const slide = root.querySelector(
        `.product-gallery__slide[data-media-id="${featuredId}"]:not(.${HIDDEN_CLASS})`
      );
      if (slide && slide.getAttribute('data-visible-index') !== null) {
        return Number(slide.getAttribute('data-visible-index'));
      }
    }

    const visibleSlides = Array.from(
      root.querySelectorAll(`.product-gallery__slide:not(.${HIDDEN_CLASS})`)
    );

    // 1. Try to find a slide explicitly and uniquely tagged to this variant (e.g. the bottle image)
    const uniqueMatch = visibleSlides.find((slide) => {
      const taggedIds = (slide.getAttribute('data-variant-ids') || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      return taggedIds.length === 1 && taggedIds.includes(String(variantId));
    });

    if (uniqueMatch && uniqueMatch.getAttribute('data-visible-index') !== null) {
      return Number(uniqueMatch.getAttribute('data-visible-index'));
    }

    // 2. Fallback to any visible media belonging to the variant
    const firstVisible = visibleSlides.find((slide) => mediaBelongsToVariant(slide, variantId));
    if (firstVisible && firstVisible.getAttribute('data-visible-index') !== null) {
      return Number(firstVisible.getAttribute('data-visible-index'));
    }

    return 0;
  }

  function filterGallery(root) {
    if (!root.querySelector('[data-variant-gallery-map]')) {
      return;
    }

    const variantId = getVariantId(root);
    if (!variantId) {
      return;
    }

    const mediaContainer = root.querySelector('[data-prodify-media-container]');
    if (!mediaContainer) {
      return;
    }

    let visibleCount = 0;
    const visibleIndexByMediaId = new Map();

    const slides = Array.from(root.querySelectorAll('.product-gallery__slide'));

    // Check if the current variant has ANY explicitly tagged images
    let hasExplicitMatch = false;
    slides.forEach((slide) => {
      const taggedIds = (slide.getAttribute('data-variant-ids') || '').split(',').map(id => id.trim()).filter(Boolean);
      if (taggedIds.includes(String(variantId))) {
        hasExplicitMatch = true;
      }
    });

    root.querySelectorAll('.product-gallery__slide').forEach((slide) => {
      const taggedIds = (slide.getAttribute('data-variant-ids') || '').split(',').map(id => id.trim()).filter(Boolean);
      let show = false;

      if (hasExplicitMatch) {
          if (taggedIds.length === 0) {
              show = false;
          } else {
              show = taggedIds.includes(String(variantId));
          }
      } else {
          if (taggedIds.length > 0) {
              show = false;
          } else {
              show = true;
          }
      }

      slide.classList.toggle(HIDDEN_CLASS, !show);

      if (show) {
        slide.setAttribute('data-visible-index', visibleCount);
        const mediaId = slide.getAttribute('data-media-id');
        if (mediaId) {
          visibleIndexByMediaId.set(String(mediaId), visibleCount);
        }
        visibleCount += 1;
      } else {
        slide.removeAttribute('data-visible-index');
      }
    });

    root.querySelectorAll('[data-gallery-thumb], .product-gallery__dot, [data-lightbox-media]').forEach((element) => {
      const taggedIds = (element.getAttribute('data-variant-ids') || '').split(',').map(id => id.trim()).filter(Boolean);
      let show = false;

      if (hasExplicitMatch) {
          if (taggedIds.length === 0) {
              show = false;
          } else {
              show = taggedIds.includes(String(variantId));
          }
      } else {
          if (taggedIds.length > 0) {
              show = false;
          } else {
              show = true;
          }
      }

      element.classList.toggle(HIDDEN_CLASS, !show);

      if (show) {
        const mediaId = element.getAttribute('data-media-id');
        const visibleIndex = mediaId ? visibleIndexByMediaId.get(String(mediaId)) : undefined;
        if (visibleIndex !== undefined) {
          element.setAttribute('data-visible-index', visibleIndex);
        } else {
          element.removeAttribute('data-visible-index');
        }
      } else {
        element.removeAttribute('data-visible-index');
        element.classList.remove('variant-gallery-thumb-active', 'is-active');
        if (element.hasAttribute('data-lightbox-media')) {
          element.style.display = 'none';
        }
      }
    });

    const showControls = visibleCount > 1;
    root.querySelectorAll('.product-gallery__nav, .product-gallery__pagination').forEach((control) => {
      control.classList.toggle(HIDDEN_CLASS, !showControls);
    });

    const activeIndex = getFeaturedVisibleIndex(root, variantId);
    setGalleryIndex(root, Math.max(visibleCount, 1), activeIndex);
    updateThumbStates(root, activeIndex);
    updateDotStates(root, activeIndex);
    syncLightboxVisibility(root, activeIndex);
    patchGalleryNavigation(root);
  }

  function handleThumbClick(event) {
    const button = event.target.closest('[data-gallery-thumb][data-visible-index]');
    if (!button || button.classList.contains(HIDDEN_CLASS)) {
      return;
    }

    const root = button.closest('.shopify-section-main-product-inner[data-prodify]');
    if (!root) {
      return;
    }

    const visibleIndex = Number(button.getAttribute('data-visible-index'));
    if (Number.isNaN(visibleIndex)) {
      return;
    }

    event.stopImmediatePropagation();

    getGalleryComponents(root).forEach((comp) => {
      if (typeof comp.goTo === 'function') {
        comp.goTo(visibleIndex);
      }
    });

    updateThumbStates(root, visibleIndex);
    updateDotStates(root, visibleIndex);
  }

  function handleDotClick(event) {
    const dot = event.target.closest('.product-gallery__dot[data-visible-index]');
    if (!dot || dot.classList.contains(HIDDEN_CLASS)) {
      return;
    }

    const root = dot.closest('.shopify-section-main-product-inner[data-prodify]');
    if (!root) {
      return;
    }

    const visibleIndex = Number(dot.getAttribute('data-visible-index'));
    if (Number.isNaN(visibleIndex)) {
      return;
    }

    event.stopImmediatePropagation();

    getGalleryComponents(root).forEach((comp) => {
      if (typeof comp.goTo === 'function') {
        comp.goTo(visibleIndex);
      }
    });

    updateThumbStates(root, visibleIndex);
    updateDotStates(root, visibleIndex);
  }

  function initRoot(root) {
    if (root.dataset.variantGalleryReady === 'true') {
      return;
    }
    root.dataset.variantGalleryReady = 'true';

    const run = () => {
      window.requestAnimationFrame(() => filterGallery(root));
    };

    run();

    root.addEventListener('change', () => {
      window.setTimeout(run, 30);
    });

    root.addEventListener('prodify:variant-media', () => {
      window.setTimeout(run, 0);
    });
  }

  function init() {
    getGalleryRoots().forEach(initRoot);
    document.addEventListener('click', handleThumbClick, true);
    document.addEventListener('click', handleDotClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (!section) {
      return;
    }

    section.querySelectorAll('.shopify-section-main-product-inner[data-prodify]').forEach((root) => {
      root.dataset.variantGalleryReady = 'false';
      initRoot(root);
    });
  });

  function initAllGalleries() {
    getGalleryRoots().forEach((root) => {
      window.requestAnimationFrame(() => filterGallery(root));
    });
  }

  if (window.Alpine) {
    initAllGalleries();
  } else {
    document.addEventListener('alpine:initialized', initAllGalleries);
  }

  // ─── Mouse-drag fix ────────────────────────────────────────────────────────
  // CSS snap-mandatory + scrollLeft direct assignment conflict during drag.
  // We intercept mousedown/mousemove/mouseup at the document level and
  // temporarily remove scroll-snap-type so the user can freely drag.
  // On release we snap to the nearest slide programmatically.
  (function installMouseDragFix() {
    let dragState = null;

    function getViewportEl(e) {
      const el = e.target.closest('.product-gallery__viewport');
      return el;
    }

    function getSlideWidth(viewport) {
      const slide = viewport.querySelector('.product-gallery__slide:not(.variant-gallery-hidden)');
      return slide ? slide.offsetWidth : viewport.clientWidth;
    }

    function snapAfterDrag(viewport, startScrollLeft, deltaX) {
      // Re-enable snap
      viewport.style.scrollSnapType = '';
      viewport.style.scrollBehavior = '';

      const slideWidth = getSlideWidth(viewport);
      if (!slideWidth) return;

      // Determine direction of drag and snap to appropriate slide
      const currentLeft = viewport.scrollLeft;
      const currentIndex = Math.round(currentLeft / slideWidth);
      const clampedIndex = Math.max(0, Math.min(currentIndex, Math.floor(viewport.scrollWidth / slideWidth) - 1));

      viewport.scrollTo({ left: clampedIndex * slideWidth, behavior: 'smooth' });
    }

    document.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      const viewport = getViewportEl(e);
      if (!viewport) return;

      // Temporarily disable scroll-snap so drag works freely
      viewport.style.scrollSnapType = 'none';
      viewport.style.scrollBehavior = 'auto';

      dragState = {
        viewport,
        startX: e.clientX,
        startScrollLeft: viewport.scrollLeft,
        moved: false,
      };
    }, { capture: true, passive: true });

    document.addEventListener('mousemove', (e) => {
      if (!dragState) return;
      const { viewport, startX, startScrollLeft } = dragState;

      const delta = e.clientX - startX;
      if (Math.abs(delta) > 4) dragState.moved = true;
      viewport.scrollLeft = startScrollLeft - delta;
    }, { capture: true, passive: true });

    function onDragEnd() {
      if (!dragState) return;
      const { viewport, startScrollLeft } = dragState;
      const delta = viewport.scrollLeft - startScrollLeft;
      dragState = null;
      snapAfterDrag(viewport, startScrollLeft, delta);
    }

    document.addEventListener('mouseup', onDragEnd, { capture: true, passive: true });
    document.addEventListener('mouseleave', (e) => {
      if (e.target === document.documentElement) onDragEnd();
    }, { capture: true, passive: true });
  })();
  // ──────────────────────────────────────────────────────────────────────────
})();
